import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import { MessageCircle, Plus, Sparkles } from "lucide-react";
import { type FC, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ExampleGoalList } from "web_myplann/Goals/Components/ExampleGoalList";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getRoute } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { EmptyStateButton } from "web_myplann/Shared/Components/EmptyStateButton";
import { useContactAdvisor } from "web_myplann/Shared/Hooks/useContactAdvisor";
import { device } from "web_myplann/Utils/device";

export const GoalsEmptyState: FC = () => {
	const theme = useTheme();
	const { palette } = theme;
	const { t } = useResource();
	const navigate = useNavigate();
	const { openChat } = useContactAdvisor();

	useEffect(() => {
		device.setStatusBarOverlay(true).catch(() => {});
		return () => {
			device.setStatusBarOverlay(false).catch(() => {});
		};
	}, []);

	return (
		<Stack>
			<Box
				position="relative"
				px={1.6}
				pt="calc(var(--mobile-safe-area-top, 0px) + 7rem)"
				pb={3}
				sx={{ background: theme.gradients.premium }}
			>
				<Box
					position="absolute"
					overflow="hidden"
					sx={{ inset: 0, pointerEvents: "none" }}
				>
					<Sparkles
						size={64}
						style={{
							position: "absolute",
							top: 64,
							right: 24,
							color: alpha(palette.darkmodeText.white, 0.05),
						}}
					/>
				</Box>

				<Stack
					alignItems="center"
					textAlign="center"
					mb={2.5}
					mt={3}
					position="relative"
				>
					<Typography variant="h2" color={palette.darkmodeText.white} mb={1}>
						{t(Resources.Goals.EmptyState.title)}
					</Typography>
					<Typography
						variant="caption"
						color={alpha(palette.darkmodeText.white, 0.7)}
						lineHeight={theme.lineHeight.relaxed}
						maxWidth={260}
					>
						{t(Resources.Goals.EmptyState.description)}
					</Typography>
				</Stack>

				<Stack gap={1} mt={5} position="relative">
					<EmptyStateButton
						icon={
							<Plus
								size={theme.iconSize.md}
								color={palette.darkmodeText.white}
							/>
						}
						iconBg={palette.blue[500]}
						label={t(Resources.Goals.EmptyState.addGoalButton)}
						onClick={() => navigate(getRoute(Routing.Goals.GoalFormCreate))}
					/>
					<EmptyStateButton
						icon={
							<MessageCircle
								size={theme.iconSize.md}
								color={palette.darkmodeText.white}
							/>
						}
						iconBg={palette.indigo[500]}
						label={t(Resources.Goals.EmptyState.getAdviceButton)}
						onClick={() =>
							openChat(t(Resources.Goals.EmptyState.advisorMessage))
						}
					/>
				</Stack>
			</Box>

			<Stack mt={2} bgcolor={palette.surface.muted}>
				<Typography
					variant="overline"
					color={palette.grey[500]}
					px={1.6}
					py={1}
				>
					{t(Resources.Goals.EmptyState.examplesTitle)}
				</Typography>

				<ExampleGoalList />
			</Stack>
		</Stack>
	);
};
